import { gql } from '@apollo/client';
import type { StrategySettings, StrategyStatus } from '@arthelper/shared/acquisition/strategy';

export interface StrategyMetrics {
  visitors: number;
  signups: number;
  waitlisted: number;
  unlocked: number;
  referredIn: number;
  referralsMade: number;
  activated: number;
  retainedD7: number;
  eligibleD7: number;
}

export interface Strategy {
  id: string;
  version: number;
  name: string;
  description: string | null;
  settings: StrategySettings;
  status: StrategyStatus;
  trafficWeight: number;
  activatedAt: string | null;
  deactivatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  metrics: StrategyMetrics;
}

const STRATEGY_FIELDS = gql`
  fragment AcquisitionStrategyFields on AcquisitionStrategy {
    id
    version
    name
    description
    settings
    status
    trafficWeight
    activatedAt
    deactivatedAt
    createdAt
    updatedAt
    metrics {
      visitors
      signups
      waitlisted
      unlocked
      referredIn
      referralsMade
      activated
      retainedD7
      eligibleD7
    }
  }
`;

// Operation names match the field names: the API's zod plugin looks a field's `validate`
// extension up by operation name and silently skips validation without a match.
export const ACQUISITION_STRATEGIES = gql`
  ${STRATEGY_FIELDS}
  query acquisitionStrategies {
    acquisitionStrategies {
      ...AcquisitionStrategyFields
    }
  }
`;

export const ACQUISITION_STRATEGY = gql`
  ${STRATEGY_FIELDS}
  query acquisitionStrategyById($id: ID!) {
    acquisitionStrategyById(id: $id) {
      ...AcquisitionStrategyFields
    }
  }
`;

export const CREATE_STRATEGY = gql`
  ${STRATEGY_FIELDS}
  mutation createAcquisitionStrategy($input: AcquisitionStrategyInput!) {
    createAcquisitionStrategy(input: $input) {
      ...AcquisitionStrategyFields
    }
  }
`;

export const UPDATE_STRATEGY = gql`
  ${STRATEGY_FIELDS}
  mutation updateAcquisitionStrategy($id: ID!, $input: AcquisitionStrategyInput!) {
    updateAcquisitionStrategy(id: $id, input: $input) {
      ...AcquisitionStrategyFields
    }
  }
`;

export const CLONE_STRATEGY = gql`
  mutation cloneAcquisitionStrategy($id: ID!) {
    cloneAcquisitionStrategy(id: $id) {
      id
      version
    }
  }
`;

export const DELETE_STRATEGY = gql`
  mutation deleteAcquisitionStrategy($id: ID!) {
    deleteAcquisitionStrategy(id: $id)
  }
`;

export const ACTIVATE_STRATEGIES = gql`
  ${STRATEGY_FIELDS}
  mutation activateAcquisitionStrategies($input: [AcquisitionActivationInput!]!) {
    activateAcquisitionStrategies(input: $input) {
      ...AcquisitionStrategyFields
    }
  }
`;

export const RELEASE_WAITLIST = gql`
  mutation releaseAcquisitionWaitlist($id: ID!) {
    releaseAcquisitionWaitlist(id: $id)
  }
`;

/** Onboarding versions an admin can pick as a strategy's waiting wizard (enabled steps only). */
export const WAITING_FLOW_VERSIONS = gql`
  query AcquisitionWaitingFlowVersions {
    onboardingVersions {
      id
      version
      name
      steps {
        stepCode
        isEnabled
      }
    }
  }
`;

export interface WaitingFlowVersion {
  id: string;
  version: number;
  name: string;
  steps: Array<{ stepCode: string; isEnabled: boolean }>;
}

export interface HeldMember {
  userId: string;
  name: string;
  email: string | null;
  slug: string | null;
  avatarUrl: string | null;
  bio: string | null;
  waitingSince: string | null;
  wizard: 'none' | 'not_started' | 'in_progress' | 'completed';
  stepsAnswered: number;
  pieces: number;
  thumbnails: string[];
  friendsJoined: number;
}

export type HeldWizard = HeldMember['wizard'];

export interface HeldPage {
  total: number;
  finished: number;
  matching: number;
  members: HeldMember[];
}

export const HELD_MEMBERS = gql`
  query acquisitionHeldMembers($strategyId: ID!, $search: String, $wizard: String, $limit: Int, $offset: Int) {
    acquisitionHeldMembers(strategyId: $strategyId, search: $search, wizard: $wizard, limit: $limit, offset: $offset) {
      total
      finished
      matching
      members {
        userId
        name
        email
        slug
        avatarUrl
        bio
        waitingSince
        wizard
        stepsAnswered
        pieces
        thumbnails
        friendsJoined
      }
    }
  }
`;

export const HELD_MEMBER_IDS = gql`
  query acquisitionHeldMemberIds($strategyId: ID!, $search: String, $wizard: String) {
    acquisitionHeldMemberIds(strategyId: $strategyId, search: $search, wizard: $wizard)
  }
`;

export const LET_IN_MEMBERS = gql`
  mutation letInAcquisitionMembers($userIds: [ID!]!) {
    letInAcquisitionMembers(userIds: $userIds)
  }
`;

export const PREVIEW_STRATEGY = gql`
  mutation previewAcquisitionStrategy($id: ID!) {
    previewAcquisitionStrategy(id: $id)
  }
`;
